'use client'

import React, { useId } from 'react'
import { ArrowUpRight } from 'lucide-react'

import type { Media, Page, Project, Product, Job, File as FileDoc } from '@/payload-types'
import { Media as MediaComponent } from '@/components/Media'
import { CMSLink } from '@/components/Link'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import { SectionWrapper } from '@/components/SectionWrapper'
import SectionHeader from '@/components/ui/SectionHeader'
import { cn } from '@/utilities/ui'

type LocationBlockProps = {
  image: string | Media
  imagePosition?: 'left' | 'right'
  bleed?: boolean
  infoTagText?: string | null
  title: string
  description?: string | null
  links?: Array<{
    link: {
      type: 'reference' | 'custom' | 'file' | 'phone' | 'email'
      reference?: {
        relationTo: 'pages' | 'projects' | 'products' | 'jobs'
        value: Page | Project | Product | Job | string | number
      } | null
      file?: FileDoc | string | null
      url?: string
      label: string
      openInNewTab?: boolean
    }
  }>
  dark?: boolean
}

export const LocationBlockClient: React.FC<LocationBlockProps> = ({
  image,
  imagePosition = 'right',
  bleed = false,
  infoTagText,
  title,
  description,
  links,
  dark = false,
}) => {
  const imageContainerId = useId()
  const isImageLeft = imagePosition === 'left'
  const padding = 'md'

  // On mobile and tablet, keep image over text; on lg+, respect side.
  const imageOrder = isImageLeft ? 'order-1' : 'order-1 lg:order-2'
  const textOrder = isImageLeft ? 'order-2' : 'order-2 lg:order-1'
  const textAlignClasses = isImageLeft
    ? 'text-left items-start lg:text-left lg:items-start'
    : 'text-left items-start lg:text-right lg:items-end'

  // Bleed only the image to the outside viewport edge on large screens
  // On mobile, use normal layout without bleed
  const bleedStyle = (() => {
    if (!bleed) return undefined
    const amount = 'calc(50vw - 50%)'
    return isImageLeft
      ? {
          marginLeft: `calc(-1 * (${amount}))`,
          width: `calc(100% + (${amount}))`,
        }
      : {
          marginRight: `calc(-1 * (${amount}))`,
          width: `calc(100% + (${amount}))`,
        }
  })()

  const content = (
    <SectionWrapper
      background={dark ? undefined : 'white'}
      isDarkMode={dark || undefined}
      padding={padding}
      className={cn(dark && 'bg-[var(--dark)]', 'overflow-hidden')}
    >
      {/* Style tag to apply bleed only on desktop */}
      {bleed && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
            #${imageContainerId} {
              margin-left: 0;
              margin-right: 0;
              width: 100%;
            }
            @media (min-width: 1024px) {
              #${imageContainerId} {
                ${isImageLeft ? `margin-left: ${bleedStyle?.marginLeft};` : `margin-right: ${bleedStyle?.marginRight};`}
                width: ${bleedStyle?.width};
              }
            }
          `,
          }}
        />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-16 items-stretch min-h-[360px] lg:min-h-[490px]">
        {/* Image */}
        <div className={cn(imageOrder, 'lg:col-span-2')}>
          {/* Mobile: normal full-width layout */}
          {/* Desktop: apply bleed effect if enabled */}
          <div id={imageContainerId} className="relative w-full h-full min-h-[360px]">
            <MotionFadeIn className="relative w-full h-full min-h-[360px]">
              {image && typeof image === 'object' && (
                <MediaComponent
                  resource={image}
                  fill
                  size={
                    bleed
                      ? '(max-width: 1023px) 100vw, 75vw'
                      : '(max-width: 1023px) 100vw, 66vw'
                  }
                  pictureClassName="absolute inset-0 w-full h-full"
                  imgClassName="object-cover"
                />
              )}
            </MotionFadeIn>
          </div>
        </div>

        {/* Text */}
        <div className={cn(textOrder, 'lg:col-span-1 py-5 lg:py-10')}>
          <MotionFadeIn
            initialY={20}
            duration={0.7}
            className={cn(
              'flex flex-col gap-4',
              textAlignClasses,
              'md:max-w-[616px] w-full self-start',
            )}
          >
            <SectionHeader
              infoTagText={infoTagText ?? undefined}
              title={title}
              subtitle={description ?? undefined}
              size="sm"
              className={cn('mb-0 w-full', !isImageLeft && 'lg:text-right')}
              titleClassName={cn(
                dark ? 'on-dark' : 'text-primary',
                !isImageLeft && 'lg:text-right',
              )}
              subtitleClassName={cn(
                'body-base whitespace-pre-line pt-4',
                dark ? 'on-dark-secondary' : 'text-secondary',
                !isImageLeft && 'lg:text-right',
              )}
            />
            {links && links.length > 0 && links[0]?.link && (
              <div className={cn('mt-6 flex', isImageLeft ? 'justify-start' : 'justify-end')}>
                <CMSLink
                  {...links[0].link}
                  newTab={links[0].link.openInNewTab}
                  appearance="inline"
                  label={null}
                  className="group inline-flex items-center gap-4"
                >
                  <span className="text-md font-ui font-medium text-brand transition-opacity duration-200 uppercase group-hover:opacity-80">
                    {links[0].link.label}
                  </span>
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center shrink-0 w-12 h-12 bg-brand text-white"
                  >
                    <ArrowUpRight
                      className={cn(
                        'w-6 h-6 transition-transform duration-300',
                        links[0].link.openInNewTab
                          ? 'rotate-45 group-hover:-rotate-90'
                          : 'rotate-90 group-hover:rotate-45',
                      )}
                    />
                  </span>
                </CMSLink>
              </div>
            )}
          </MotionFadeIn>
        </div>
      </div>
    </SectionWrapper>
  )

  return content
}
